'use client';

import React, { useState } from 'react';
import AppointmentTypeSelector from './AppointmentTypeSelector';
import AppointmentCalendar from './AppointmentCalendar';
import PreAppointmentQuestionnaire from './PreAppointmentQuestionnaire';
import AppointmentSummary from './AppointmentSummary';
import WaitlistNotification from './WaitlistNotification';
import ConfirmationModal from './ConfirmationModal';

interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
  counselorId?: string;
  counselorName?: string;
}

type Step = 'type' | 'calendar' | 'questionnaire' | 'summary';

const AppointmentSchedulingInteractive = () => {
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState<Record<string, string>>({});
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
    setCurrentStep('calendar');
  };

  const handleSlotSelect = (date: string, slot: TimeSlot) => {
    if (!slot.available) {
      setShowWaitlist(true);
      return;
    }
    setSelectedDate(date);
    setSelectedSlot(slot);
    setCurrentStep('questionnaire');
  };

  const handleQuestionnaireSubmit = (responses: Record<string, string>) => {
    setQuestionnaireResponses(responses);
    setCurrentStep('summary');
  };

  const handleConfirmAppointment = () => {
    setShowConfirmation(true);
  };

  const handleEditAppointment = () => {
    setCurrentStep('type');
  };

  const handleJoinWaitlist = () => {
    setShowWaitlist(false);
    alert('You have been added to the waitlist. We will notify you when a slot becomes available.');
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setCurrentStep('type');
    setSelectedType(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setQuestionnaireResponses({});
  };

  return (
    <>
      <div data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:74:6" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="74" data-component-end-line="106" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
        {currentStep === 'type' && (
          <AppointmentTypeSelector data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:76:10" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="76" data-component-end-line="79" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="AppointmentTypeSelector" data-component-content="%7B%22elementName%22%3A%22AppointmentTypeSelector%22%7D"
            selectedType={selectedType}
            onTypeSelect={handleTypeSelect}
          />
        )}

        {currentStep === 'calendar' && (
          <AppointmentCalendar data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:83:10" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="83" data-component-end-line="87" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="AppointmentCalendar" data-component-content="%7B%22elementName%22%3A%22AppointmentCalendar%22%7D"
            onSlotSelect={handleSlotSelect}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
          />
        )}

        {currentStep === 'questionnaire' && (
          <PreAppointmentQuestionnaire data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:91:10" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="91" data-component-end-line="94" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="PreAppointmentQuestionnaire" data-component-content="%7B%22elementName%22%3A%22PreAppointmentQuestionnaire%22%7D"
            appointmentType={selectedType}
            onSubmit={handleQuestionnaireSubmit}
          />
        )}

        {currentStep === 'summary' && (
          <AppointmentSummary data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:98:10" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="98" data-component-end-line="104" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="AppointmentSummary" data-component-content="%7B%22elementName%22%3A%22AppointmentSummary%22%7D"
            appointmentType={selectedType}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            onConfirm={handleConfirmAppointment}
            onEdit={handleEditAppointment}
          />
        )}
      </div>

      <WaitlistNotification data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:108:6" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="108" data-component-end-line="112" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="WaitlistNotification" data-component-content="%7B%22elementName%22%3A%22WaitlistNotification%22%7D"
        show={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        onJoinWaitlist={handleJoinWaitlist}
      />

      <ConfirmationModal data-component-id="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx:114:6" data-component-path="src/app/appointment-scheduling-system/components/AppointmentSchedulingInteractive.tsx" data-component-line="114" data-component-end-line="117" data-component-file="AppointmentSchedulingInteractive.tsx" data-component-name="ConfirmationModal" data-component-content="%7B%22elementName%22%3A%22ConfirmationModal%22%7D"
        show={showConfirmation}
        onClose={handleCloseConfirmation}
      />
    </>
  );
};

export default AppointmentSchedulingInteractive;